/**
 * SharePoint Browser Component
 * A reusable component for browsing SharePoint sites, libraries, and folders
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Button,
  Checkbox
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  Storage as StorageIcon,
  NavigateNext as NavigateNextIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import Icon from '@mui/material/Icon';
import { sharePointService } from './services/sharePointService';
import type {
  SharePointBrowserProps,
  SharePointSite,
  SharePointLibrary,
  SharePointItem,
  SharePointBrowseResult,
  SharePointItemType
} from './types/sharepoint';

const escapeRegExp = (value: string): string => value.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&');

const patternToRegex = (pattern: string): RegExp => {
  const trimmed = pattern.trim();
  if (!trimmed) {
    return new RegExp('^$', 'i');
  }

  const escaped = escapeRegExp(trimmed).replace(/\\\*/g, '.*');
  return new RegExp(`^${escaped}$`, 'i');
};

const normalizeItemPath = (item: SharePointItem): string | null => {
  if (!item.path) {
    return item.name;
  }

  // Graph paths look like /drive/root:/Folder/Sub
  const withoutDrive = item.path.replace(/^\/drive\/root:?/i, '').replace(/^:/, '').replace(/:$/, '');
  const cleaned = withoutDrive.replace(/^\/+/, '');
  if (!cleaned) {
    return item.name;
  }
  return `${cleaned.replace(/\/+$/, '')}/${item.name}`;
};

export const SharePointBrowser: React.FC<SharePointBrowserProps> = ({
  mode = 'file',
  initialSite,
  initialDrive,
  initialDriveName,
  initialPath = '',
  onSelect,
  onCancel,
  fileTypeFilter,
  multiSelect = false,
  title,
  description,
  showSearch = true,
  // showPreview = false, // Reserved for future use
  defaultItemType,
  hideFilters,
  autoHideFilters = false,
  hideBreadcrumb = false,
  showTitle = true,
  hiddenItemPatterns = []
}) => {
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Navigation state
  const [sites, setSites] = useState<SharePointSite[]>([]);
  const [selectedSite, setSelectedSite] = useState<SharePointSite | null>(null);
  const [libraries, setLibraries] = useState<SharePointLibrary[]>([]);
  const [selectedLibrary, setSelectedLibrary] = useState<SharePointLibrary | null>(null);
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [browseResult, setBrowseResult] = useState<SharePointBrowseResult | null>(null);

  // Selection state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [hoveredItem, setHoveredItem] = useState<string | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars

  useEffect(() => {
    setSelectedItems(new Set());
  }, [selectedSite?.id, selectedLibrary?.id, currentPath]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // View state
  const defaultItemTypeValue: SharePointItemType = defaultItemType ?? (mode === 'folder' ? 'folders' : 'all');
  const itemTypePreset = defaultItemType !== undefined;

  const [itemTypeFilter, setItemTypeFilter] = useState<SharePointItemType>(
    defaultItemTypeValue
  );

  useEffect(() => {
    setItemTypeFilter(defaultItemTypeValue);
  }, [defaultItemTypeValue]);

  const hiddenItemRegexes = useMemo(
    () =>
      hiddenItemPatterns
        .map(pattern => pattern.trim())
        .filter(Boolean)
        .map(patternToRegex),
    [hiddenItemPatterns]
  );

  const isItemHidden = useCallback(
    (item: SharePointItem) => {
      if (!hiddenItemRegexes.length) {
        return false;
      }

      const candidates: string[] = [item.name];
      if (item.path) {
        candidates.push(item.path);
      }
      const normalizedPath = normalizeItemPath(item);
      if (normalizedPath) {
        candidates.push(normalizedPath);
      }

      return hiddenItemRegexes.some(regex =>
        candidates.some(candidate => regex.test(candidate))
      );
    },
    [hiddenItemRegexes]
  );

  const filterHiddenItems = useCallback(
    (items: SharePointItem[] = []) => items.filter(item => !isItemHidden(item)),
    [isItemHidden]
  );

  const filtersPreset = Boolean(initialSite && (initialDrive || initialDriveName) && itemTypePreset);
  const shouldHideFilters = hideFilters ?? (autoHideFilters && filtersPreset);

  // Load sites on mount
  useEffect(() => {
    loadSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle initial site selection
  useEffect(() => {
    if (initialSite && sites.length > 0) {
      const site = sites.find(s => s.id === initialSite);
      if (site) {
        setSelectedSite(site);
      }
    }
  }, [initialSite, sites]);

  // Load libraries when site is selected
  useEffect(() => {
    if (selectedSite) {
      loadLibraries(selectedSite.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite]);

  // Handle initial library selection
  useEffect(() => {
    if (libraries.length === 0) {
      return;
    }

    let library = initialDrive
      ? libraries.find(l => l.id === initialDrive)
      : undefined;

    if (!library && initialDriveName) {
      library = libraries.find(
        (l) => l.name.toLowerCase() === initialDriveName.toLowerCase()
      );
    }

    if (!library && !selectedLibrary) {
      library = libraries[0];
    }

    if (library && (!selectedLibrary || selectedLibrary.id !== library.id)) {
      setSelectedLibrary(library);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDrive, initialDriveName, libraries]);

  useEffect(() => {
    setCurrentPath(initialPath);
  }, [initialPath]);

  // Browse folder when library or path changes
  useEffect(() => {
    if (selectedSite && selectedLibrary) {
      browseFolder(currentPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite, selectedLibrary, currentPath, itemTypeFilter]);

  const loadSites = async () => {
    setLoading(true);
    setError(null);
    try {
      const loadedSites = await sharePointService.getSites();
      setSites(loadedSites);
      if (loadedSites.length === 1 && !selectedSite) {
        setSelectedSite(loadedSites[0]);
      }
    } catch (err: any) {
      setError('Failed to load SharePoint sites');
      console.error('Error loading sites:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLibraries = async (siteKey: string) => {
    setLoading(true);
    setError(null);
    try {
      const loadedLibraries = await sharePointService.getLibraries(siteKey);
      setLibraries(loadedLibraries);
      if (loadedLibraries.length === 1 && !selectedLibrary) {
        setSelectedLibrary(loadedLibraries[0]);
      }
    } catch (err: any) {
      setError('Failed to load document libraries');
      console.error('Error loading libraries:', err);
    } finally {
      setLoading(false);
    }
  };

  const browseFolder = async (path: string) => {
    if (!selectedSite || !selectedLibrary) return;

    setLoading(true);
    setError(null);
    setSearchQuery('');

    try {
      const result = await sharePointService.browseFolder(
        selectedSite.id,
        selectedLibrary.id,
        path,
        itemTypeFilter
      );
      const filteredItems = filterHiddenItems(result.items);
      setBrowseResult({
        ...result,
        items: filteredItems,
        itemCount: filteredItems.length
      });
      setCurrentPath(path);
    } catch (err: any) {
      setError('Failed to browse folder');
      console.error('Error browsing folder:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item: SharePointItem) => {
    if (item.type === 'folder') {
      // Navigate into folder
      const newPath = sharePointService.buildItemPath(item, currentPath);
      setCurrentPath(newPath);
    } else if (mode === 'file') {
      // File selection in file mode
      if (multiSelect) {
        const newSelection = new Set(selectedItems);
        if (newSelection.has(item.id)) {
          newSelection.delete(item.id);
        } else {
          newSelection.add(item.id);
        }
        setSelectedItems(newSelection);
      } else {
        // Single selection - immediate callback
        if (onSelect && selectedSite && selectedLibrary) {
          onSelect(item, selectedSite, selectedLibrary.id);
        }
      }
    }
  };

  const handleFolderSelect = (item: SharePointItem) => {
    if (mode === 'folder' && item.type === 'folder') {
      if (onSelect && selectedSite && selectedLibrary) {
        onSelect(item, selectedSite, selectedLibrary.id);
      }
    }
  };

  const handleBreadcrumbClick = (path: string) => {
    setCurrentPath(path);
  };

  const handleRefresh = () => {
    if (selectedSite && selectedLibrary) {
      browseFolder(currentPath);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleMultiSelectConfirm = () => {
    if (onSelect && selectedSite && selectedLibrary && selectedItems.size > 0) {
      const items = displayItems.filter(item => selectedItems.has(item.id));

      items.forEach(item => {
        onSelect(item, selectedSite, selectedLibrary.id);
      });
    }
  };

  const renderItem = (item: SharePointItem) => {
    const isSelected = selectedItems.has(item.id);
    const isHovered = hoveredItem === item.id;
    const matchesFilter = sharePointService.matchesFileTypeFilter(item, fileTypeFilter);

    if (!matchesFilter) {
      return null;
    }

    const iconName = sharePointService.getFileIcon(item);

    return (
      <ListItem
        key={item.id}
        disablePadding
        onMouseEnter={() => setHoveredItem(item.id)}
        onMouseLeave={() => setHoveredItem(null)}
        sx={{
          backgroundColor: isSelected ? 'action.selected' : 'inherit',
          '&:hover': { backgroundColor: 'action.hover' }
        }}
      >
        <ListItemButton
          onClick={() => handleItemClick(item)}
          disabled={mode === 'folder' && item.type === 'file'}
        >
          {multiSelect && mode === 'file' && item.type === 'file' && (
            <Checkbox
              edge="start"
              checked={isSelected}
              tabIndex={-1}
              disableRipple
            />
          )}
          <ListItemIcon>
            <Icon>{iconName}</Icon>
          </ListItemIcon>
          <ListItemText
            primary={item.name}
            secondaryTypographyProps={{ component: 'div' }}
            secondary={
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {sharePointService.formatDate(item.lastModified)}
                </Typography>
                {item.type === 'file' && (
                  <Typography variant="caption" color="text.secondary">
                    {sharePointService.formatFileSize(item.size)}
                  </Typography>
                )}
                {item.type === 'folder' && item.childCount !== undefined && (
                  <Typography variant="caption" color="text.secondary">
                    {item.childCount} items
                  </Typography>
                )}
              </Stack>
            }
          />
          {mode === 'folder' && item.type === 'folder' && (
            <ListItemSecondaryAction>
              <Tooltip title="Select this folder">
                <IconButton
                  edge="end"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFolderSelect(item);
                  }}
                  sx={{
                    color: isHovered ? 'success.main' : 'inherit',
                    '@keyframes pulseSharePointGreen': {
                      '0%': {
                        boxShadow: '0 0 0 0 rgba(46, 125, 50, 0.4)'
                      },
                      '70%': {
                        boxShadow: '0 0 0 10px rgba(46, 125, 50, 0)'
                      },
                      '100%': {
                        boxShadow: '0 0 0 0 rgba(46, 125, 50, 0)'
                      }
                    },
                    animation: isHovered ? 'pulseSharePointGreen 1.2s ease-in-out infinite' : 'none',
                    transition: 'color 0.2s ease-in-out'
                  }}
                >
                  <CheckIcon />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  // Client-side filtering based on search query
  const displayItems = useMemo(() => {
    const items = browseResult?.items || [];
    if (!searchQuery.trim()) {
      return items;
    }

    const query = searchQuery.toLowerCase();
    return items.filter(item =>
      item.name.toLowerCase().includes(query)
    );
  }, [browseResult?.items, searchQuery]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          p: shouldHideFilters ? 1.5 : 2,
          pb: showSearch && selectedLibrary ? 1.5 : 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        {showTitle && title && (
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
        )}
        {description && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {description}
          </Typography>
        )}

        {/* Site and Library Selection */}
        {!shouldHideFilters && (
          <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>SharePoint Site</InputLabel>
              <Select
                value={selectedSite?.id || ''}
                label="SharePoint Site"
                onChange={(e) => {
                  const site = sites.find(s => s.id === e.target.value);
                  setSelectedSite(site || null);
                  setSelectedLibrary(null);
                  setCurrentPath('');
                }}
              >
                {sites.map(site => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedSite && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Document Library</InputLabel>
                <Select
                  value={selectedLibrary?.id || ''}
                  label="Document Library"
                  onChange={(e) => {
                    const library = libraries.find(l => l.id === e.target.value);
                    setSelectedLibrary(library || null);
                    setCurrentPath('');
                  }}
                >
                  {libraries.map(library => (
                    <MenuItem key={library.id} value={library.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StorageIcon fontSize="small" />
                        <span>{library.name}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Show</InputLabel>
              <Select
                value={itemTypeFilter}
                label="Show"
                onChange={(e) => setItemTypeFilter(e.target.value as SharePointItemType)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="folders">Folders Only</MenuItem>
                <MenuItem value="files">Files Only</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        )}

        {/* Breadcrumb Navigation */}
        {!hideBreadcrumb && selectedLibrary && browseResult && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: showSearch ? 1.5 : 0.75, gap: 1 }}
          >
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              {browseResult.breadcrumb.map((crumb, index) => (
                <Link
                  key={crumb.path}
                  component="button"
                  variant="body2"
                  onClick={() => handleBreadcrumbClick(crumb.path)}
                  underline="hover"
                  color={index === browseResult.breadcrumb.length - 1 ? 'text.primary' : 'inherit'}
                >
                  {index === 0 ? <HomeIcon fontSize="small" /> : crumb.name}
                </Link>
              ))}
            </Breadcrumbs>
            <IconButton onClick={handleRefresh} disabled={!selectedLibrary} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}

        {/* Search Bar */}
        {showSearch && selectedLibrary && (
          <TextField
            fullWidth
            size="small"
            placeholder="Search for files and folders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        )}
      </Box>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !selectedSite && (
          <Alert severity="info">
            Please select a SharePoint site to browse
          </Alert>
        )}

        {!loading && selectedSite && !selectedLibrary && (
          <Alert severity="info">
            Please select a document library to browse
          </Alert>
        )}

        {!loading && selectedLibrary && displayItems.length === 0 && (
          <Alert severity="info">
            {searchQuery.trim() ? 'No items found matching your search' : 'This folder is empty'}
          </Alert>
        )}

        {!loading && displayItems.length > 0 && (
          <>
            {searchQuery.trim() && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Found {displayItems.length} items matching "{searchQuery}"
              </Alert>
            )}
            <List>
              {displayItems.map(renderItem)}
            </List>
          </>
        )}
      </Box>

      {/* Footer Actions */}
      {(multiSelect || onCancel) && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {multiSelect && selectedItems.size > 0 && (
              <Chip
                label={`${selectedItems.size} selected`}
                color="primary"
                onDelete={() => setSelectedItems(new Set())}
              />
            )}
            {onCancel && (
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={onCancel}
              >
                Cancel
              </Button>
            )}
            {multiSelect && (
              <Button
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={handleMultiSelectConfirm}
                disabled={selectedItems.size === 0}
              >
                Select ({selectedItems.size})
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
